import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { useScrollAnimation } from 'hooks/useScrollAnimation';
import { PartnerModal } from '../auth/PartnerModal';

export const UniversityPartnership = () => {
    const { t } = useTranslation();
    const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);

    const benefits = [
        t('universityPartnership.benefit1'),
        t('universityPartnership.benefit2'),
        t('universityPartnership.benefit3'),
        t('universityPartnership.benefit4'),
        t('universityPartnership.benefit5'),
        t('universityPartnership.benefit6'),
    ];

    return (
        <section id="universities" className="py-20 bg-white overflow-hidden">
            <div className="container mx-auto px-4" ref={ref}>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Image Side */}
                    <div className={`order-2 lg:order-1 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                            <img
                                src="/university-students.jpg"
                                alt={t('universityPartnership.imageAlt')}
                                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                    </div>

                    {/* Content Side */}
                    <div className={`order-1 lg:order-2 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{ transitionDelay: '0.3s' }}>
                        <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6 animate-bounce-gentle">
                            {t('universityPartnership.badge')}
                        </span>

                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 leading-tight">
                            {t('universityPartnership.title')}
                        </h2>

                        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                            {t('universityPartnership.description')}
                        </p>

                        <ul className="space-y-4 mb-8">
                            {benefits.map((benefit, index) => (
                                <li
                                    key={index}
                                    className={`flex items-start gap-3 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                                    style={{ transitionDelay: isVisible ? `${0.5 + index * 0.15}s` : '0s' }}
                                >
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5 group-hover:bg-primary/20 transition-colors">
                                        <Check className="w-4 h-4 text-primary" />
                                    </div>
                                    <span className="text-gray-700">{benefit}</span>
                                </li>
                            ))}
                        </ul>

                        <div className={`flex flex-wrap gap-4 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '1.4s' }}>
                            <Button
                                className="bg-primary text-white hover:bg-primary/90 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                                onClick={() => setIsPartnerModalOpen(true)}
                            >
                                {t('universityPartnership.partnerCta')}
                                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <PartnerModal
                isOpen={isPartnerModalOpen}
                onClose={() => setIsPartnerModalOpen(false)}
            />
        </section>
    );
};
